import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { Toggle } from "@/components/Toggle/Toggle";
import { classNames } from "@/utils/classNames";

interface IncludeSignatureToggleProps {
	/** Whether the draft goes out with the mailbox's signature. */
	checked: boolean;
	/** Whether the sending mailbox has a signature at all. */
	hasSignature: boolean;
	onChange: (checked: boolean) => void;
}

/**
 * The email draft's "Include signature" switch (PREC-4401). It starts from the company's Copywriting
 * setting; the send places the signature through `applyEmailSignature` (apps/api), once. A mailbox
 * without a signature has nothing to include, so the switch is off and names where one is set, by
 * the labels the user sees: the composer also runs in the Chrome extension on LinkedIn, where an
 * app-relative link would open a LinkedIn page.
 */
export const IncludeSignatureToggle: FunctionComponent<IncludeSignatureToggleProps> = ({
	checked,
	hasSignature,
	onChange,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`inbox.signature.${key}`);

	return (
		<div className="flex flex-wrap items-center gap-2 px-3 py-1.5 bg-brand-white border-x border-border-light text-xs text-text-meta">
			<label
				className={classNames(
					"flex items-center gap-2 text-text",
					hasSignature ? "cursor-pointer" : "cursor-not-allowed"
				)}
			>
				<Toggle
					disabled={!hasSignature}
					isChecked={hasSignature && checked}
					name="includeSignature"
					testId="include-signature-toggle"
					onChange={() => onChange(!checked)}
				/>
				{ts("label")}
			</label>
			{!hasSignature && <span data-testid="include-signature-missing">{ts("noSignature")}</span>}
		</div>
	);
};
